import React, { useCallback, useEffect, useRef } from 'react';
import { GalleryItem } from '@/types';
import { getMediaUrl } from '@/services/api';

interface GalleryModalProps {
  gallery: GalleryItem | null;
  currentImageIndex: number;
  isAutoRotating: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onToggleAutoRotate: () => void;
  className?: string;
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  gallery,
  currentImageIndex,
  isAutoRotating,
  onClose,
  onNext,
  onPrev,
  onToggleAutoRotate,
  className = ''
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowRight') {
      onNext();
    } else if (e.key === 'ArrowLeft') {
      onPrev();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      onToggleAutoRotate();
    }
  }, [onClose, onNext, onPrev, onToggleAutoRotate]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add/remove event listeners
  useEffect(() => {
    if (gallery) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gallery, handleKeyDown]);

  if (!gallery) return null;

  return (
    <div 
      className={`gallery-modal-overlay ${className}`}
      onClick={handleOverlayClick}
      ref={modalRef}
    >
      <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
        <div className="gallery-slideshow">
          <button 
            className="nav-btn prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            aria-label="Previous image"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          
          <div className="slide-container">
            <img 
              src={getMediaUrl(gallery.images[currentImageIndex])} 
              alt={`${gallery.title} - Image ${currentImageIndex + 1}`}
              className="modal-image"
              loading="lazy"
            />
            <div className="slide-caption">
              {currentImageIndex + 1} / {gallery.images.length}
            </div>
          </div>
          
          <button 
            className="nav-btn next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            aria-label="Next image"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
        
        <div className="modal-controls">
          <button 
            className={`rotation-btn ${isAutoRotating ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleAutoRotate();
            }}
            aria-label={isAutoRotating ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isAutoRotating ? '❚❚ Pause' : '▶ Play'}
          </button>
          
          <button 
            className="close-btn" 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close gallery"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
